<template>
    <div data-testid="liberar-asiento-view">

        <!-- Vista de eventos -->
        <template v-if="viewMode === 'eventos'">
            <header class="tw:mb-6">
                <p v-if="nombreAbonado" class="tw:text-slate-700 tw:text-sm tw:font-semibold tw:mt-1">
                    {{ nombreAbonado }}
                    <span v-if="nAbonado" class="tw:text-slate-400 tw:font-normal">({{ $t('perfil.n_abonado') }}: {{ nAbonado }})</span>
                </p>
                <p class="tw:text-slate-500">{{ $t('perfil.liberar_selecciona_evento') }}</p>
                <span class="tw:text-xs">{{ $t('perfil.asiento_liberado_terminos_club') }}</span>
            </header>

            <!-- Loading state -->
            <div v-if="loadingEventos" class="tw:space-y-4">
                <SkeletonItem class="tw:w-56 tw:h-5 tw:mb-6" />
                <div v-for="n in 2" :key="n" class="tw:bg-white tw:p-5 tw:rounded-2xl tw:border tw:border-gray-100 tw:shadow-sm tw:flex tw:flex-col tw:md:flex-row tw:md:items-center tw:gap-6">
                    <div class="tw:shrink-0 tw:flex tw:md:flex-col tw:items-center tw:justify-center tw:bg-gray-50 tw:rounded-xl tw:h-16 tw:w-16">
                        <SkeletonItem class="tw:w-8 tw:h-8 tw:rounded" />
                    </div>
                    <div class="tw:flex-grow">
                        <SkeletonItem class="tw:w-32 tw:h-4 tw:mb-2" />
                        <SkeletonItem class="tw:w-48 tw:h-5" />
                    </div>
                    <div class="tw:shrink-0">
                        <SkeletonItem class="tw:w-48 tw:h-12 tw:rounded-xl" />
                    </div>
                </div>
            </div>

            <!-- Lista de eventos -->
            <div v-else-if="hasEventos" class="tw:space-y-4" data-testid="liberar-evento-list">
                <h2 class="tw:text-sm tw:font-bold tw:text-slate-400 tw:uppercase tw:tracking-widest tw:mb-6">{{ $t('perfil.liberar_proximos_eventos') }}</h2>
                <div
                    v-for="evento in eventos"
                    :key="evento.id"
                    :data-testid="`liberar-evento-card-${evento.id}`"
                    class="tw:bg-white tw:p-3 tw:rounded-2xl tw:border tw:border-gray-100 hover:tw:border-[var(--altcolor)]/30 tw:transition-all tw:flex tw:flex-col tw:md:flex-row tw:md:items-center tw:gap-4 tw:md:gap-6 tw:justify-between tw:shadow-sm"
                >
                    <div class="tw:flex tw:gap-4">
                        <!-- Fecha destacada -->
                        <div class="tw:shrink-0 tw:flex tw:md:flex-col tw:items-center tw:justify-center tw:bg-gray-50 tw:rounded-xl tw:px-4 tw:py-2 tw:h-14 tw:w-14">
                            <span class="tw:text-2xl tw:font-black tw:text-slate-900 tw:leading-none">{{ getDay(evento.fecha) }}</span>
                            <span class="tw:text-xs tw:font-bold tw:text-slate-400 tw:uppercase">{{ getMonth(evento.fecha) }}</span>
                        </div>

                        <!-- Info del evento -->
                        <div class="tw:flex-grow">
                            <h4 class="tw:text-lg tw:font-bold tw:text-slate-900 tw:m-0">
                                {{ evento.equipo_local?.nombre }} {{ $t('perfil.liberar_separador_equipos') }} {{ evento.equipo_visitante?.nombre }}
                            </h4>
                            <div class="tw:flex tw:items-center tw:gap-2 tw:text-xs tw:text-slate-600">
                                <span>{{ evento.hora }}</span>
                                <p class="tw:m-0">
                                    {{ evento.fecha }}
                                </p>
                            </div>
                        
                            <Button
                                v-if="evento.synopsis"
                                variant="default"
                                custom-class="tw:mt-2 tw:text-xs tw:font-semibold tw:px-2 tw:py-1"
                                @click="showEventoInfo(evento)"
                            >
                                {{ $t('partidos.info') }}
                            </Button>
                        </div>
                    </div>

                    <!-- Botón -->
                    <div class="tw:flex tw:flex-col tw:w-full tw:md:w-72 tw:gap-1">
                        <Button
                            :data-testid="`liberar-evento-btn-${evento.id}`"
                            variant="alt"
                            custom-class="tw:w-full tw:md:w-auto tw:rounded-xl tw:px-6 tw:py-3 tw:font-bold tw:transition-all tw:duration-300"
                            icon-name="event_busy"
                            icon-name-class="tw:text-lg"
                            @click="selectEvento(evento)"
                        >
                            {{ $t('perfil.liberar_btn_evento') }}
                        </Button>
                        <span v-if="evento.importe_monedero_abonado && evento.importe_monedero_abonado > 0" class="tw:text-[10px] tw:text-amber-600 tw:m-0 tw:italic">
                            {{ $t('perfil.liberar_reembolso_texto', { importe: refundUsesPoints ? `${refundAmount(evento)} ${$t('perfil.liberar_puntos')}` : `${refundAmount(evento)} €` }) }}
                        </span>
                    </div>
                </div>
            </div>

            <!-- Sin eventos -->
            <div v-else class="tw:bg-white tw:p-5 tw:rounded-2xl tw:border tw:border-gray-100 tw:shadow-sm tw:py-16 tw:text-center">
                <i class="material-icons tw:text-5xl tw:text-slate-300">event_busy</i>
                <p class="tw:mt-4 tw:font-semibold tw:text-slate-700">
                    {{ $t('perfil.asiento_liberado_no_sesion') }}
                </p>
            </div>
        </template>

        <!-- Vista de sesiones -->
        <template v-else-if="viewMode === 'sesiones'">
            <header>
                <h1 class="tw:text-xl tw:font-black tw:text-slate-900 tw:tracking-tight tw:mb-4">{{ $t('perfil.liberar_titulo_sesion') }}</h1>

                <!-- Info del abonado -->
                <div v-if="nombreAbonado" class="tw:flex tw:flex-col tw:gap-1 tw:mb-2">
                    <p class="tw:text-slate-700 tw:text-sm tw:font-semibold tw:m-0">
                        {{ nombreAbonado }}
                        <span v-if="nAbonado" class="tw:text-slate-400 tw:font-normal">({{ $t('perfil.n_abonado') }}: {{ nAbonado }})</span>
                    </p>
                </div>

                <!-- Info del evento seleccionado -->
                <div class="tw:flex tw:flex-col tw:gap-1">
                    <div class="tw:flex tw:flex-wrap tw:items-center tw:gap-3">
                        <h2 class="tw:text-xl tw:font-bold tw:text-slate-900 tw:uppercase">
                            {{ selectedEvento.equipo_local?.nombre }}
                            <span class="tw:text-[var(--altcolor)]">{{ $t('perfil.liberar_separador_equipos').toUpperCase() }}</span>
                            {{ selectedEvento.equipo_visitante?.nombre }}
                        </h2>
                    </div>
                    <span class="tw:text-xs tw:mb-3">{{ $t('perfil.asiento_liberado_terminos_club') }}</span>
                </div>
            </header>

            <!-- Loading state -->
            <div v-if="loadingSesiones" class="tw:space-y-4">
                <SkeletonItem class="tw:w-48 tw:h-5 tw:mb-6" />
                <div v-for="n in 2" :key="n" class="tw:bg-white tw:p-5 tw:rounded-2xl tw:border tw:border-gray-100 tw:shadow-sm tw:flex tw:flex-col tw:md:flex-row tw:md:items-center tw:gap-6">
                    <div class="tw:shrink-0 tw:flex tw:md:flex-col tw:items-center tw:justify-center tw:bg-gray-50 tw:rounded-xl tw:h-16 tw:w-16">
                        <SkeletonItem class="tw:w-8 tw:h-8 tw:rounded" />
                    </div>
                    <div class="tw:flex-grow">
                        <SkeletonItem class="tw:w-32 tw:h-4 tw:mb-2" />
                        <SkeletonItem class="tw:w-48 tw:h-5" />
                    </div>
                    <div class="tw:shrink-0">
                        <SkeletonItem class="tw:w-48 tw:h-12 tw:rounded-xl" />
                    </div>
                </div>
            </div>

            <!-- Lista de sesiones -->
            <div v-else-if="hasSesiones" class="tw:space-y-4" data-testid="liberar-sesion-list">
                <h2 class="tw:text-sm tw:font-bold tw:text-slate-400 tw:uppercase tw:tracking-widest tw:mb-6">{{ $t('perfil.liberar_sesiones_disponibles') }}</h2>
                <div
                    v-for="sesion in sesiones"
                    :key="sesion.id"
                    :data-testid="`liberar-sesion-card-${sesion.id}`"
                    class="tw:bg-white tw:p-5 tw:rounded-2xl tw:border tw:border-gray-100 hover:tw:border-[var(--altcolor)]/30 tw:transition-all tw:flex tw:flex-col tw:md:flex-row tw:md:items-center tw:gap-6 tw:shadow-sm"
                >
                    <!-- Fecha destacada -->
                    <div class="tw:shrink-0 tw:flex tw:md:flex-col tw:items-center tw:justify-center tw:bg-gray-50 tw:rounded-xl tw:px-4 tw:py-2 tw:h-20 tw:w-20">
                        <span class="tw:text-2xl tw:font-black tw:text-slate-900 tw:leading-none">{{ getDay(sesion.fecha) }}</span>
                        <span class="tw:text-xs tw:font-bold tw:text-slate-400 tw:uppercase">{{ getMonth(sesion.fecha) }}</span>
                    </div>

                    <!-- Info de la sesión -->
                    <div class="tw:flex-grow">
                        <div class="tw:flex tw:items-center tw:gap-2 tw:mb-1">
                            <span class="tw:inline-flex tw:items-center tw:px-2 tw:py-0.5 tw:text-xs tw:font-bold tw:text-[var(--altcolor)] tw:uppercase">{{ getDayName(sesion.fecha) }}</span>
                            <span class="tw:text-xs tw:text-slate-600">{{ sesion.hora }}</span>
                        </div>
                        <h4 class="tw:text-lg tw:font-bold tw:text-slate-900 tw:uppercase">
                            {{ selectedEvento.equipo_local?.nombre }} {{ $t('perfil.liberar_separador_equipos').toUpperCase() }} {{ selectedEvento.equipo_visitante?.nombre }}
                        </h4>
                    </div>

                    <!-- Botón -->
                    <div class="tw:flex tw:flex-col tw:w-full tw:md:w-72 tw:gap-1">
                        <Button
                            :data-testid="`liberar-sesion-btn-${sesion.id}`"
                            v-if="!sesion.asiento_liberado"
                            variant="alt"
                            custom-class="tw:w-full tw:md:w-auto tw:rounded-xl tw:px-6 tw:py-3 tw:font-bold tw:transition-all tw:duration-300"
                            icon-name="event_seat"
                            icon-name-class="tw:text-xl"
                            @click="openLiberarModal(sesion)"
                        >
                            {{ $t('perfil.liberar_asiento') }}
                        </Button>
                        <Button
                            v-else
                            variant="success"
                            custom-class="tw:w-full tw:md:w-auto tw:rounded-xl tw:px-6 tw:py-3 tw:font-bold tw:transition-all tw:duration-300"
                            icon-name="check_circle"
                            icon-name-class="tw:text-xl"
                            disabled
                        >
                            {{ $t('perfil.asiento_liberado') }}
                        </Button>
                    </div>
                </div>
            </div>

            <!-- Sin sesiones -->
            <div v-else-if="isSesionesEmpty" class="tw:bg-white tw:p-5 tw:rounded-2xl tw:border tw:border-gray-100 tw:shadow-sm tw:py-16 tw:text-center">
                <i class="material-icons tw:text-5xl tw:text-slate-300">event_busy</i>
                <p class="tw:mt-4 tw:font-semibold tw:text-slate-700">
                    {{ $t('perfil.asiento_liberado_no_sesion') }}
                </p>
            </div>
        </template>

        <!-- Modal de información del evento -->
        <ModalInformacion
            :show="showInfoModal"
            :title="$t('partidos.info')"
            :message="infoEvento?.synopsis || ''"
            :politica-menores="infoEvento?.underage_policy || ''"
            @close="closeInfoModal"
        />
    </div>
</template>

<script setup>
    import { ref, watch } from 'vue';
    import { getDay, getMonth, getDayName } from '@/utils/dateUtils';
    import { SkeletonItem } from '@/modules/front/ui/skeleton';
    import ModalInformacion from '@/modules/front/ui/ModalInformacion.vue';
    import Button from '@/modules/front/ui/Button.vue';

    // Estado local para el modal de información del evento
    const showInfoModal = ref(false);
    const infoEvento = ref(null);

    const props = defineProps({
        viewMode: {
            type: String,
            required: true
        },
        selectedEvento: {
            type: Object,
            default: null
        },
        eventos: {
            type: Array,
            default: () => []
        },
        sesiones: {
            type: Array,
            default: () => []
        },
        loadingEventos: {
            type: Boolean,
            default: false
        },
        loadingSesiones: {
            type: Boolean,
            default: false
        },
        errorEventos: {
            type: String,
            default: null
        },
        errorSesiones: {
            type: String,
            default: null
        },
        hasEventos: {
            type: Boolean,
            default: false
        },
        hasSesiones: {
            type: Boolean,
            default: false
        },
        isSesionesEmpty: {
            type: Boolean,
            default: false
        },
        refundAmount: {
            type: Function,
            default: () => 0
        },
        refundUsesPoints: {
            type: Boolean,
            default: false
        },
        nombreAbonado: {
            type: String,
            default: null
        },
        nAbonado: {
            type: [String, Number],
            default: null
        }
    });

    const emit = defineEmits([
        'select-evento',
        'open-liberar-modal'
    ]);

    const selectEvento = evento => {
        emit('select-evento', evento);
    };

    const openLiberarModal = sesion => {
        emit('open-liberar-modal', sesion);
    };

    // Modal de información del evento
    const showEventoInfo = evento => {
        infoEvento.value = evento;
        showInfoModal.value = true;
    };

    const closeInfoModal = () => {
        showInfoModal.value = false;
        infoEvento.value = null;
    };

    watch(() => props.errorEventos, err => {
        if (err) console.error(err);
    });

    watch(() => props.errorSesiones, err => {
        if (err) console.error(err);
    });
</script>